import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Clock, Ban, AlertCircle } from 'lucide-react';

const QRRedirect = () => {
  const { shortCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    if (shortCode) {
      handleQRScan();
    } else {
      setError('No QR code specified');
      setLoading(false);
    }
  }, [shortCode]);

  const detectDeviceInfo = () => {
    const userAgent = navigator?.userAgent || '';
    let deviceType = 'unknown';
    let browserName = 'Unknown';
    let osName = 'Unknown';

    // Device type detection
    if (/tablet|ipad|playbook|silk/i?.test(userAgent)) {
      deviceType = 'tablet';
    } else if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i?.test(userAgent)) {
      deviceType = 'mobile';
    } else {
      deviceType = 'desktop';
    }

    // Browser detection
    if (userAgent?.includes('Chrome')) browserName = 'Chrome';
    else if (userAgent?.includes('Firefox')) browserName = 'Firefox';
    else if (userAgent?.includes('Safari')) browserName = 'Safari';
    else if (userAgent?.includes('Edge')) browserName = 'Edge';
    else if (userAgent?.includes('Opera')) browserName = 'Opera';

    // OS detection
    if (userAgent?.includes('Windows')) osName = 'Windows';
    else if (userAgent?.includes('Mac')) osName = 'macOS';
    else if (userAgent?.includes('Linux')) osName = 'Linux';
    else if (userAgent?.includes('Android')) osName = 'Android';
    else if (userAgent?.includes('iOS')) osName = 'iOS';

    return { deviceType, browserName, osName };
  };

  const getLocationInfo = async () => {
    try {
      // Use ipapi.co for location info
      const response = await fetch('https://ipapi.co/json/');
      if (response?.ok) {
        const data = await response?.json();
        return {
          country_code: data?.country_code || null,
          country_name: data?.country_name || null,
          city: data?.city || null,
          ip: data?.ip || null
        };
      }
    } catch (error) {
      // Silent fail for location info
    }
    return { country_code: null, country_name: null, city: null, ip: null };
  };

  const handleQRScan = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!shortCode) {
        throw new Error('No short code provided');
      }

      // Get device and location info in parallel for speed
      const [deviceInfo, locationInfo] = await Promise.all([
        Promise.resolve(detectDeviceInfo()),
        getLocationInfo()
      ]);

      let scanResult, scanError;
      
      // Try the new debug function first for better error reporting
      const { data: debugResult, error: debugError } = await supabase?.rpc('record_qr_scan_with_debug', {
        qr_short_code: shortCode,
        user_agent: navigator?.userAgent || null,
        ip_address: locationInfo?.ip,
        location_data: {
          country_code: locationInfo?.country_code,
          country_name: locationInfo?.country_name,
          city: locationInfo?.city,
          device_type: deviceInfo?.deviceType,
          browser_name: deviceInfo?.browserName,
          os_name: deviceInfo?.osName,
          referrer: document?.referrer || null
        }
      });

      if (debugError) {
        // Try enhanced function
        const { data: enhancedResult, error: enhancedError } = await supabase?.rpc('record_qr_scan_with_swapped_logic_v2', {
          qr_short_code: shortCode,
          user_agent: navigator?.userAgent || null,
          ip_address: locationInfo?.ip,
          location_data: {
            country_code: locationInfo?.country_code,
            country_name: locationInfo?.country_name,
            city: locationInfo?.city,
            device_type: deviceInfo?.deviceType,
            browser_name: deviceInfo?.browserName,
            os_name: deviceInfo?.osName,
            referrer: document?.referrer || null
          }
        });

        if (enhancedError) {
          // Fallback to original function
          const { data: originalResult, error: originalError } = await supabase?.rpc('record_qr_scan_with_swapped_logic', {
            qr_short_code: shortCode,
            user_agent: navigator?.userAgent || null,
            ip_address: locationInfo?.ip,
            location_data: {
              country_code: locationInfo?.country_code,
              country_name: locationInfo?.country_name,
              city: locationInfo?.city,
              device_type: deviceInfo?.deviceType,
              browser_name: deviceInfo?.browserName,
              os_name: deviceInfo?.osName,
              referrer: document?.referrer || null
            }
          });
          
          scanResult = originalResult;
          scanError = originalError;
        } else {
          scanResult = enhancedResult;
          scanError = enhancedError;
        }
      } else {
        scanResult = debugResult;
        scanError = debugError;
      }

      // Store debug info if available
      if (scanResult?.[0]?.debug_info) {
        setDebugInfo(scanResult?.[0]?.debug_info);
      }

      if (scanError) {
        // If it's an authorization error, try to get diagnostic info
        if (scanError?.message?.includes('authorization') || scanError?.message?.includes('permission')) {
          try {
            const { data: diagnosticData, error: diagnosticError } = await supabase?.rpc('diagnose_qr_redirect', {
              qr_short_code: shortCode
            });
            
            if (diagnosticData && diagnosticData?.length > 0) {
              const diagnosis = diagnosticData?.[0];
              
              if (diagnosis?.issue_found) {
                setError(`QR Code Issues Found: ${diagnosis?.issues?.join(', ')}`);
              } else {
                setError('QR code appears valid but authorization failed. Please try again.');
              }
              
              setDebugInfo({
                ...(debugInfo || {}),
                diagnostic: diagnosis
              });
            } else {
              setError(`Authorization failed: ${scanError?.message}`);
            }
          } catch (diagError) {
            setError(`QR scan failed: ${scanError?.message}`);
          }
        } else {
          setError(`QR scan failed: ${scanError?.message}`);
        }
        
        setLoading(false);
        return;
      }

      // Handle scan result
      if (!scanResult || scanResult?.length === 0) {
        setError('QR code not found or no scan result returned');
        setLoading(false);
        return;
      }

      const result = scanResult?.[0];

      if (!result?.success) {
        let errorMessage = result?.message || 'QR scan validation failed';
        
        // Enhanced error handling with debug info
        if (result?.debug_info) {
          setDebugInfo(result?.debug_info);
        }
        
        // For expired or limit reached QR codes, try to get more info
        if (errorMessage?.includes('expired') || errorMessage?.includes('limit')) {
          try {
            const { data: qrInfo } = await supabase?.rpc('get_qr_code_with_status', { 
              qr_code_uuid: result?.scan_id 
            });

            if (qrInfo && qrInfo?.length > 0) {
              setQrData({
                ...qrInfo?.[0],
                is_expired: errorMessage?.includes('expired'),
                is_scan_limit_reached: errorMessage?.includes('limit')
              });
            }
          } catch (infoError) {
            // Silent fail for additional info
          }
        }
        
        setError(errorMessage);
        setLoading(false);
        return;
      }

      // SUCCESS CASE - IMMEDIATE redirect to content URL
      const redirectUrl = result?.redirect_url;
      
      if (redirectUrl && redirectUrl?.trim() !== '') {
        // Validate URL format
        let finalUrl = redirectUrl?.trim();
        
        // Ensure URL has proper protocol
        if (!finalUrl?.includes('://') && !finalUrl?.startsWith('mailto:') && !finalUrl?.startsWith('tel:')) {
          finalUrl = `https://${finalUrl}`;
        }
        
        // IMMEDIATE redirect for faster user experience
        window.location.href = finalUrl;
        
      } else {
        setError('No destination URL configured for this QR code');
        setLoading(false);
      }

    } catch (error) {
      let errorMessage = 'An error occurred while processing the QR code';
      
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        errorMessage = 'Cannot connect to QR service. Please check your connection and try again.';
      } else if (error?.message?.includes('Not found')) {
        errorMessage = 'QR code not found. It may have been deleted or deactivated.';
      } else if (error?.message?.includes('authorization') || error?.message?.includes('permission')) {
        errorMessage = 'Failed to perform authorization check. Your Supabase project may be paused or there may be a permissions issue.';
      } else if (error?.message) {
        errorMessage = error?.message;
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  const getErrorIcon = () => {
    if (qrData?.is_expired) return <Clock size={48} className="text-orange-500" />;
    if (qrData?.is_scan_limit_reached) return <Ban size={48} className="text-red-500" />;
    return <AlertCircle size={48} className="text-red-500" />;
  };

  const getErrorTitle = () => {
    if (qrData?.is_expired) return 'QR Code Expired';
    if (qrData?.is_scan_limit_reached) return 'Scan Limit Reached';
    return 'QR Code Unavailable';
  };

  const getErrorMessage = () => {
    if (qrData?.is_expired) {
      return `This QR code expired on ${new Date(qrData?.expiry_date)?.toLocaleDateString()}. Please contact the QR code owner for a new code.`;
    }
    if (qrData?.is_scan_limit_reached) {
      return `This QR code has reached its maximum scan limit of ${qrData?.max_scans} scans. Please contact the QR code owner.`;
    }
    return error || 'This QR code is currently unavailable.';
  };

  // SIMPLIFIED LOADING STATE - Only spinner for faster UX
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            {getErrorIcon()}
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {getErrorTitle()}
          </h2>
          
          <p className="text-gray-600 mb-6 leading-relaxed">
            {getErrorMessage()}
          </p>

          {qrData && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm text-left">
              <h3 className="font-semibold text-gray-900 mb-2">QR Code Details:</h3>
              <div className="space-y-1 text-gray-600">
                <p><span className="font-medium">Name:</span> {qrData?.name}</p>
                <p><span className="font-medium">Type:</span> {qrData?.qr_type}</p>
                {qrData?.expiry_date && (
                  <p><span className="font-medium">Expiry:</span> {new Date(qrData?.expiry_date)?.toLocaleDateString()}</p>
                )}
                {qrData?.max_scans && (
                  <p><span className="font-medium">Scan Limit:</span> {qrData?.current_scan_count} / {qrData?.max_scans}</p>
                )}
              </div>
            </div>
          )}

          {/* DEBUG INFO FOR DEVELOPMENT */}
          {debugInfo && process.env?.NODE_ENV === 'development' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-left">
              <h3 className="font-semibold text-blue-900 mb-2">Debug Information:</h3>
              <pre className="text-blue-700 text-xs overflow-auto max-h-40">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={() => navigate('/')}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Go to Homepage
            </button>
            <button
              onClick={() => window.history?.back()}
              className="w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default QRRedirect;